import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, ChevronDown, FileEdit, Send, Eye, Clock, CheckCircle2, XCircle, AlertTriangle, CalendarClock, CalendarDays, RefreshCw, FileText, ArrowUp, ArrowDown, ChevronsUpDown, Bookmark, Pencil, Check, BellOff } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { confirmDialog } from "@/lib/dialogs";
import DatePicker, { parseDate } from "@/components/DatePicker";
import { ProposalStageFlow } from "@/components/ProposalStageFlow";

const STATUSES = ["draft", "sent", "viewed", "accepted", "rejected", "superseded", "withdrawn", "expired"];

// Stage tiles shown at the top of both Proposals and Renewals (mirrors the dashboard funnel).
const STAGE_TILES = [
  { key: "draft", label: "Draft", statuses: ["draft"], Icon: FileEdit, ring: "bg-slate-100 text-slate-500" },
  { key: "sent", label: "Sent", statuses: ["sent"], Icon: Send, ring: "bg-sky-50 text-sky-500" },
  { key: "viewed", label: "Viewed", statuses: ["viewed"], Icon: Eye, ring: "bg-indigo-50 text-indigo-500" },
  { key: "awaiting", label: "Awaiting signature", statuses: ["acceptance_started"], Icon: Clock, ring: "bg-amber-50 text-amber-500" },
  { key: "accepted", label: "Accepted", statuses: ["accepted"], Icon: CheckCircle2, ring: "bg-emerald-50 text-emerald-500" },
  { key: "declined", label: "Rejected / expired", statuses: ["rejected", "expired"], Icon: XCircle, ring: "bg-rose-50 text-rose-500" },
];

// MRR after any client-facing discount (mirrors ProposalDetail summary & backend pricing).
const discountedMonthly = (p) => {
  const monthly = Number(p.totals?.monthly_recurring) || 0;
  const d = p.discount;
  let amt = 0;
  if (d && Number(d.value) > 0) {
    amt = d.type === "percent" ? monthly * Number(d.value) / 100 : Number(d.value);
    amt = Math.min(amt, monthly);
  }
  return Math.round((monthly - amt) * 100) / 100;
};

const EVENT_FOR_STATUS = {
  draft: ["created"], sent: ["sent"], viewed: ["first_viewed"],
  accepted: ["accepted_signed", "accepted_manual"], rejected: ["rejected"], expired: ["expired"],
};

const fmtActionDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }).replace(/ /g, "-");
};

const statusDate = (p) => {
  const acts = p.activity || [];
  const evs = EVENT_FOR_STATUS[p.status];
  if (evs) {
    const m = [...acts].reverse().find((a) => evs.includes(a.event));
    if (m?.at) return m.at;
  }
  const last = acts.length ? acts[acts.length - 1].at : null;
  return last || p.updated_at || p.issue_date;
};

const renewalTone = (days) => (days <= 30 ? "text-red-600" : days <= 60 ? "text-orange-600" : "text-cass-slate");

function SortHead({ label, k, sort, onSort, align = "left" }) {
  const active = sort.key === k;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={align === "right" ? "text-right" : ""}>
      <button onClick={() => onSort(k)} data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 hover:text-cass-navy transition-colors ${align === "right" ? "flex-row-reverse" : ""} ${active ? "text-cass-navy font-semibold" : ""}`}>
        {label}<Icon className={`w-3.5 h-3.5 ${active ? "opacity-90" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
}

function StageTiles({ proposals, statusSel, onPick }) {
  const active = (t) => t.statuses.length === statusSel.length && t.statuses.every((s) => statusSel.includes(s));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stage-tiles">
      {STAGE_TILES.map((t) => {
        const n = proposals.filter((p) => t.statuses.includes(p.status)).length;
        return (
          <button
            key={t.key}
            data-testid={`stage-tile-${t.key}`}
            onClick={() => onPick(t)}
            className={`text-left bg-white border rounded-2xl p-4 flex items-center gap-3 transition-colors ${active(t) ? "border-cass-teal ring-1 ring-cass-teal" : "border-border hover:border-cass-teal"}`}
          >
            <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${t.ring}`}><t.Icon className="w-5 h-5" /></span>
            <div className="min-w-0">
              <div className="text-2xl font-bold text-cass-navy font-alt tabular leading-none">{n}</div>
              <div className="text-[11px] text-cass-slate mt-1 leading-tight">{t.label}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Renewal-date buckets shown on the Renewals page — auto-flags accounts approaching renewal.
const RENEWAL_BUCKETS = [
  { key: "overdue", label: "Overdue", test: (d) => d < 0, Icon: AlertTriangle, ring: "bg-red-100 text-red-600" },
  { key: "red", label: "Due within 1 month", test: (d) => d >= 0 && d <= 30, Icon: CalendarClock, ring: "bg-red-50 text-red-500" },
  { key: "amber", label: "Due within 2 months", test: (d) => d > 30 && d <= 60, Icon: CalendarDays, ring: "bg-amber-50 text-amber-500" },
];

function RenewalTimingTiles({ rows, filter, onPick }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="renewal-timing-tiles">
      {RENEWAL_BUCKETS.map((b) => {
        const n = rows.filter((r) => b.test(r.days_until)).length;
        const active = filter === b.key;
        return (
          <button
            key={b.key}
            data-testid={`renewal-tile-${b.key}`}
            onClick={() => onPick(b.key)}
            className={`text-left bg-white border rounded-2xl p-4 flex items-center gap-3 transition-colors ${active ? "border-cass-teal ring-1 ring-cass-teal" : "border-border hover:border-cass-teal"} ${b.key === "overdue" && n > 0 ? "ring-1 ring-red-200" : ""}`}
          >
            <span className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${b.ring}`}><b.Icon className="w-5 h-5" /></span>
            <div className="min-w-0">
              <div className={`text-2xl font-bold font-alt tabular leading-none ${b.key === "overdue" && n > 0 ? "text-red-600" : "text-cass-navy"}`}>{n}</div>
              <div className="text-[11px] text-cass-slate mt-1 leading-tight">{b.label}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function Proposals() {
  const [proposals, setProposals] = useState([]);
  const [renewalsDue, setRenewalsDue] = useState([]);
  const [params] = useSearchParams();
  const isRenewals = params.get("type") === "renewal";
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [pauseUntil, setPauseUntil] = useState("");
  const statusKey = `cass.proposalStatusSel.${isRenewals ? "renewal" : "new"}`;
  const [renewTab, setRenewTab] = useState("all"); // renewals mode: "all" | "due"
  const [renewFilter, setRenewFilter] = useState(null); // renewals-due bucket: overdue | red | amber
  const [statusSel, setStatusSel] = useState([]);
  const navigate = useNavigate();

  // Remember the chosen statuses per mode (new vs renewal) across visits.
  useEffect(() => {
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(statusKey)) || []; } catch { saved = []; }
    setStatusSel(saved);
    setRenewTab("all");
    setRenewFilter(null);
  }, [isRenewals, statusKey]);

  useEffect(() => {
    try { localStorage.setItem(statusKey, JSON.stringify(statusSel)); } catch { /* ignore */ }
  }, [statusSel, statusKey]);

  // Saved views — personal named filter presets (status combos) stored locally.
  const [views, setViews] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cass.proposalViews")) || []; } catch { return []; }
  });
  useEffect(() => { try { localStorage.setItem("cass.proposalViews", JSON.stringify(views)); } catch { /* ignore */ } }, [views]);
  const [newViewName, setNewViewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const saveView = () => {
    const name = newViewName.trim();
    if (!name) return;
    setViews((vs) => [...vs.filter((v) => v.name !== name), { name, statuses: statusSel }]);
    setNewViewName("");
    toast.success(`View "${name}" saved`);
  };
  const applyView = (v) => setStatusSel(v.statuses || []);
  const deleteView = (name) => setViews((vs) => vs.filter((v) => v.name !== name));
  const renameView = (oldName) => {
    const nm = editName.trim();
    if (nm && nm !== oldName) setViews((vs) => vs.map((v) => (v.name === oldName ? { ...v, name: nm } : v)));
    setEditing(null);
  };

  const load = () => {
    api.get("/proposals", { params: { proposal_type: isRenewals ? "renewal" : "new" } }).then((r) => setProposals(r.data));
  };
  useEffect(() => { load(); }, [isRenewals]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isRenewals) api.get("/renewals-due").then((r) => setRenewalsDue(r.data || [])).catch(() => {}); }, [isRenewals]);

  const toggleStatus = (s) => setStatusSel((xs) => (xs.includes(s) ? xs.filter((x) => x !== s) : [...xs, s]));
  const pickStage = (t) => {
    const isActive = t.statuses.length === statusSel.length && t.statuses.every((s) => statusSel.includes(s));
    setStatusSel(isActive ? [] : t.statuses);
    if (isRenewals) { setRenewTab("all"); setRenewFilter(null); }
  };

  const del = async (e, p) => {
    e.stopPropagation();
    if (!(await confirmDialog({ title: "Delete draft?", description: `Delete ${p.reference} for ${p.client_name}? This permanently removes the draft. Only drafts can be deleted — a sent proposal can only be rejected.`, confirmText: "Delete", tone: "destructive" }))) return;
    try {
      const { data } = await api.delete(`/proposals/${p.id}`);
      toast.success(data.number_reset ? `Draft deleted — reference ${data.reference} freed for reuse` : "Draft deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  const shown = statusSel.length ? proposals.filter((p) => statusSel.includes(p.status)) : proposals;
  const showDue = isRenewals && renewTab === "due";

  const [sort, setSort] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("cass.proposalSort")); if (s?.key) return s; } catch { /* ignore */ }
    return { key: "date", dir: "desc" };
  });
  useEffect(() => { try { localStorage.setItem("cass.proposalSort", JSON.stringify(sort)); } catch { /* ignore */ } }, [sort]);
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "date" || key === "mrr" ? "desc" : "asc" }));
  const sortVal = (p, key) => {
    switch (key) {
      case "reference": return (p.reference || "").toLowerCase();
      case "client": return (p.client_name || "").toLowerCase();
      case "owner": return (p.owner_name || "").toLowerCase();
      case "date": return new Date(p.issue_date || p.created_at || 0).getTime();
      case "mrr": return discountedMonthly(p);
      case "status": return p.status || "";
      default: return "";
    }
  };
  const sortedShown = [...shown].sort((a, b) => {
    const av = sortVal(a, sort.key), bv = sortVal(b, sort.key);
    const c = av < bv ? -1 : av > bv ? 1 : 0;
    return sort.dir === "asc" ? c : -c;
  });

  const activeShownIds = sortedShown.filter((p) => ["sent", "viewed", "acceptance_started"].includes(p.status)).map((p) => p.id);
  const toISO = (v) => { const d = parseDate(v); return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : v; };
  const isPaused = (p) => p.reminders_paused_until && new Date(p.reminders_paused_until) > new Date();
  const bulkPause = async (resume = false) => {
    if (!activeShownIds.length) { toast.info("No active (sent/viewed/awaiting) proposals in this view"); return; }
    if (!resume && !pauseUntil) { toast.error("Pick a resume date first"); return; }
    const n = activeShownIds.length;
    const ok = await confirmDialog({
      title: resume ? `Resume reminders for ${n} proposal${n === 1 ? "" : "s"}?` : `Pause reminders for ${n} proposal${n === 1 ? "" : "s"}?`,
      description: resume
        ? "Automatic reminders will resume immediately for every active proposal currently shown by your filters."
        : `Automatic reminders for the ${n} active proposal${n === 1 ? "" : "s"} currently shown will pause and then resume automatically on ${pauseUntil}.`,
      confirmText: resume ? "Resume" : "Pause",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/proposals/bulk-reminder-pause", { ids: activeShownIds, until: resume ? null : toISO(pauseUntil) });
      toast.success(resume ? `Reminders resumed for ${data.affected} proposal${data.affected === 1 ? "" : "s"}` : `Reminders paused for ${data.affected} proposal${data.affected === 1 ? "" : "s"} until ${data.until}`);
      if (!resume) setPauseUntil("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update reminders"); }
  };

  return (
    <div>
      <PageHeader
        title={isRenewals ? "Renewals" : "Proposals"}
        accent={isRenewals ? "orange" : "teal"}
        Icon={isRenewals ? RefreshCw : FileText}
        subtitle={isRenewals ? "Renewal proposals and accounts coming up for renewal." : "New-business proposals across every stage."}
        actions={<Button onClick={() => navigate("/proposals/new")} className={`text-white ${isRenewals ? "bg-orange-500 hover:bg-orange-600" : "bg-cass-teal hover:bg-teal-600"}`} data-testid="new-proposal-list-btn"><Plus className="w-4 h-4 mr-2" />New</Button>}
      />
      <div className="p-8 space-y-4">
        {/* Proposals: stage tiles. Renewals: proposal-status stage tiles AND renewal-date flags. */}
        {!isRenewals && <StageTiles proposals={proposals} statusSel={statusSel} onPick={pickStage} />}
        {isRenewals && (
          <div className="space-y-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-cass-slate mb-2">By status</div>
              <StageTiles proposals={proposals} statusSel={statusSel} onPick={pickStage} />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-cass-slate mb-2">By renewal date</div>
              <RenewalTimingTiles rows={renewalsDue} filter={renewTab === "due" ? renewFilter : null} onPick={(k) => { setRenewTab("due"); setRenewFilter((f) => (f === k ? null : k)); }} />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          {isRenewals ? (
            <Tabs value={renewTab} onValueChange={(v) => { setRenewTab(v); if (v === "all") setRenewFilter(null); }}>
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-renewals-all">All renewals</TabsTrigger>
                <TabsTrigger value="due" data-testid="tab-renewals-due">
                  Renewals Due
                  {renewalsDue.length > 0 && (
                    <span className={`ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[11px] font-semibold text-white ${renewalsDue.some((r) => r.days_until <= 30) ? "bg-red-600" : "bg-orange-500"}`} data-testid="renewals-due-count">
                      {renewalsDue.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          ) : <div />}
          {!showDue && (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-between font-normal" data-testid="saved-views-btn">
                    <Bookmark className="w-4 h-4 mr-1.5 opacity-70" />Views{views.length ? ` (${views.length})` : ""}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="end">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-cass-slate px-1 pb-1">Saved views</div>
                  {views.length === 0 && <div className="px-1 py-2 text-xs text-cass-slate">No saved views yet.</div>}
                  {views.map((v) => (
                    <div key={v.name} className="flex items-center gap-1 px-1 py-0.5 rounded hover:bg-cass-bone" data-testid={`view-row-${v.name}`}>
                      {editing === v.name ? (
                        <>
                          <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && renameView(v.name)} className="h-7 text-sm" autoFocus data-testid="view-rename-input" />
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => renameView(v.name)} data-testid="view-rename-save"><Check className="w-3.5 h-3.5 text-emerald-600" /></Button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => applyView(v)} className="flex-1 text-left text-sm text-cass-navy truncate py-1" data-testid={`view-apply-${v.name}`}>{v.name} <span className="text-[10px] text-cass-slate">({v.statuses?.length ? v.statuses.length : "all"})</span></button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => { setEditing(v.name); setEditName(v.name); }} data-testid={`view-rename-${v.name}`}><Pencil className="w-3.5 h-3.5 text-cass-slate" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => deleteView(v.name)} data-testid={`view-delete-${v.name}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                        </>
                      )}
                    </div>
                  ))}
                  <div className="h-px bg-border my-2" />
                  <div className="flex items-center gap-1">
                    <Input value={newViewName} onChange={(e) => setNewViewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveView()} placeholder="Name this view" className="h-8 text-sm" data-testid="view-name-input" />
                    <Button size="sm" onClick={saveView} disabled={!newViewName.trim()} className="bg-cass-teal text-white h-8 shrink-0" data-testid="view-save-btn">Save</Button>
                  </div>
                  <p className="text-[10px] text-cass-slate mt-1.5 px-1">Saves the current status filter{statusSel.length ? ` (${statusSel.length} selected)` : " (all statuses)"}.</p>
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-48 justify-between font-normal" data-testid="status-filter">
                    <span className="truncate">{statusSel.length ? `${statusSel.length} status${statusSel.length > 1 ? "es" : ""} selected` : "All statuses"}</span>
                    <ChevronDown className="w-4 h-4 opacity-60 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <button onClick={() => setStatusSel([])} className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-cass-bone ${statusSel.length ? "text-cass-slate" : "text-cass-navy font-medium"}`} data-testid="status-opt-all">All statuses</button>
                  <div className="h-px bg-border my-1" />
                  {STATUSES.map((s) => (
                    <label key={s} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-cass-bone cursor-pointer text-sm" data-testid={`status-opt-${s}`}>
                      <Checkbox checked={statusSel.includes(s)} onCheckedChange={() => toggleStatus(s)} />
                      <span className="capitalize">{s}</span>
                    </label>
                  ))}
                </PopoverContent>
              </Popover>
              {isAdmin && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-between font-normal text-cass-navy data-[state=open]:bg-cass-bone" data-testid="bulk-pause-btn"><BellOff className="w-4 h-4 mr-1.5 opacity-70" />Reminders</Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="end">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-cass-slate pb-1">Pause reminders</div>
                    <p className="text-xs text-cass-slate mb-2">Applies to the <b>{activeShownIds.length}</b> active proposal{activeShownIds.length === 1 ? "" : "s"} shown by your current filters. They auto-resume on the date you set.</p>
                    <label className="text-xs text-cass-slate">Resume on</label>
                    <div className="mt-1"><DatePicker value={pauseUntil} onChange={setPauseUntil} testId="bulk-pause-date" /></div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="bg-cass-navy text-white flex-1" onClick={() => bulkPause(false)} data-testid="bulk-pause-apply">Pause</Button>
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => bulkPause(true)} data-testid="bulk-pause-resume">Resume all shown</Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}
        </div>

        {showDue ? (
          <div className="space-y-6">
            {[
              { key: "overdue", title: "Overdue", rows: renewalsDue.filter((r) => r.days_until < 0), dot: "bg-red-600" },
              { key: "red", title: "Due within 1 month", rows: renewalsDue.filter((r) => r.days_until >= 0 && r.days_until <= 30), dot: "bg-red-500" },
              { key: "amber", title: "Due within 2 months", rows: renewalsDue.filter((r) => r.days_until > 30 && r.days_until <= 60), dot: "bg-amber-500" },
            ].filter((sec) => !renewFilter || sec.key === renewFilter).map((sec) => (
              <div key={sec.key} data-testid={`renewal-section-${sec.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${sec.dot}`} />
                  <h3 className="font-alt font-semibold text-cass-navy">{sec.title}</h3>
                  <span className="text-sm text-cass-slate">({sec.rows.length})</span>
                </div>
                <div className="bg-white border border-border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-orange-50/70 hover:bg-orange-50/70">
                        <TableHead>Reference</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead className="text-right">MRR</TableHead>
                        <TableHead className="text-right">Renewal due</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sec.rows.map((r) => { const rid = r.id || r.client_id; return (
                        <TableRow key={rid} data-testid={`renewal-row-${rid}`} className="cursor-pointer hover:bg-cass-bone" onClick={() => navigate(r.migrated ? `/clients/${r.client_id}` : `/proposals/${r.id}`)}>
                          <TableCell className="font-medium text-cass-navy">{r.reference}{r.migrated && <Badge variant="outline" className="ml-2 text-[10px] border-slate-300 text-cass-slate" data-testid={`renewal-migrated-${rid}`}>No proposal</Badge>}</TableCell>
                          <TableCell>{r.client_name}</TableCell>
                          <TableCell className="text-sm text-cass-slate">{r.owner_name}</TableCell>
                          <TableCell className="text-right tabular">{gbp(discountedMonthly(r))}</TableCell>
                          <TableCell className="text-right">
                            <span className={`tabular font-semibold ${renewalTone(r.days_until)}`} data-testid={`renewal-date-${rid}`}>{fmtActionDate(r.renewal_date)}</span>
                            <div className={`text-[11px] ${renewalTone(r.days_until)}`}>{r.days_until < 0 ? `${Math.abs(r.days_until)}d overdue` : `in ${r.days_until}d`}</div>
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="outline" className="border-cass-teal/40 text-cass-teal hover:bg-cass-teal/5 whitespace-nowrap" data-testid={`create-renewal-${rid}`}
                              onClick={() => navigate(`/proposals/new?type=renewal&client=${r.client_id}${r.migrated ? "" : `&renewalOf=${r.id}&recurringOnly=1`}`)}>
                              <RefreshCw className="w-3.5 h-3.5 mr-1" />Create renewal
                            </Button>
                          </TableCell>
                        </TableRow>
                      ); })}
                      {!sec.rows.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-cass-slate">Nothing {sec.key === "overdue" ? "overdue" : sec.key === "red" ? "due within 1 month" : "in the 1–2 month window"}.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className={isRenewals ? "bg-orange-50/70 hover:bg-orange-50/70" : "bg-cass-teal/5 hover:bg-cass-teal/5"}>
                  <SortHead label="Reference" k="reference" sort={sort} onSort={toggleSort} />
                  <SortHead label="Client" k="client" sort={sort} onSort={toggleSort} />
                  <SortHead label="Owner" k="owner" sort={sort} onSort={toggleSort} />
                  <SortHead label="Date" k="date" sort={sort} onSort={toggleSort} />
                  <SortHead label="MRR" k="mrr" sort={sort} onSort={toggleSort} align="right" />
                  <SortHead label="Status" k="status" sort={sort} onSort={toggleSort} />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedShown.map((p) => (
                  <TableRow key={p.id} data-testid={`proposal-row-${p.id}`} className="cursor-pointer hover:bg-cass-bone" onClick={() => navigate(`/proposals/${p.id}`)}>
                    <TableCell className="font-medium text-cass-navy">{p.reference}{isPaused(p) && <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 bg-amber-50 text-amber-600" title={`Reminders paused until ${p.reminders_paused_until}`} data-testid={`paused-badge-${p.id}`}><BellOff className="w-3 h-3 mr-0.5" />Paused</Badge>}</TableCell>
                    <TableCell>{p.client_name}</TableCell>
                    <TableCell className="text-sm text-cass-slate">{p.owner_name}</TableCell>
                    <TableCell className="text-sm text-cass-slate tabular" data-testid={`proposal-date-${p.id}`}>{fmtActionDate(p.issue_date || p.created_at)}</TableCell>
                    <TableCell className="text-right tabular">{gbp(discountedMonthly(p))}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <ProposalStageFlow proposal={p} />
                        <span className="text-[11px] text-cass-slate capitalize" data-testid={`status-date-${p.id}`}>{p.status} · {fmtActionDate(statusDate(p))}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {p.status === "draft" && (
                        <Button size="sm" variant="ghost" title="Delete draft" onClick={(e) => del(e, p)} data-testid={`proposal-delete-${p.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!shown.length && <TableRow><TableCell colSpan={7} className="text-center py-10 text-cass-slate">No {isRenewals ? "renewals" : "proposals"} found.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
